import { HttpParams } from '@angular/common/http';
import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ComponentBase } from '../../../../../core/_infrastructure/classes/componentBase';
import { ExceptionManager } from '../../../../../core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from '../../../../../core/_infrastructure/classes/services/auth.service';
import { NotificationService } from '../../../../../core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from '../../../../../core/components/navmenu/navmenu.service';
import { GlobalService } from '../../../../../core/_infrastructure/classes/services/global.service';
import { Tc } from '../../../../../core/_infrastructure/types/common';
import { PeriodoDatosCaService } from '../datosca.service';
import { DatosCaPeriodoCamViewModel, PeriodoEntidadAuditoraCamViewModel } from '../datosca.types';
import { RegEntidadAuditoraItemViewModel } from '../../../../registros/entidadesauditoras/entidadauditora.types';
import { EntidadAuditoraPopupComponent } from '../../../../../core/components/popup/entidadesauditoras/entidadAuditoraPopup.component';
import { ConfirmationService } from 'primeng/api';
import { EnumCodigosTwTcAcciones } from '../../../../../_importacion/solicitudes/types/enum';
import { PeriodoCamService } from '../../datosbasicos/periodocam.service';
import { EnumIdsTcEstadoPvCam } from '../../../../../_campanias/types/enum';
import { PeriodoCamViewModel } from '../../datosbasicos/periodocam.types';
import { UsuariosService } from '../../../../usuarios/usuarios.service';

@Component({
    selector: 'app-datosca-wrapper-edit',
    templateUrl: './datosca-wrapper-edit.component.html',
})
export class DatosCaWrapperEditComponent extends ComponentBase implements OnInit {
    /**
     * @article: Reactive forms with smart dumb components
     * https://medium.com/angular-athens/reactive-forms-with-smart-dumb-components-48ebc81417c7
     */

    @ViewChild(EntidadAuditoraPopupComponent) private entidadAuditoraPopupComponent: EntidadAuditoraPopupComponent;

    comunidades: Tc[];
    usuariosCaAceptacion: Tc[];
    usuariosCaUnidadInspeccion: Tc[];
    usuariosCaAforoCorte: Tc[];
    usuariosCaAceptacionRequired = false;
    usuariosCaUnidadInspeccionRequired = false;
    usuariosCaAforoCorteRequired = false;
    visibilitySpinner = false;
    datoscaPeriodo: DatosCaPeriodoCamViewModel;
    IdPeriodo: any;
    IdConf: any;
    IdCampania: any;
    IdCa: number = 0;
    successMessage = 'Datos C.A. guardados correctamente';
    addEntidadEnabled = false;
    listaEntidadesPeriodo: PeriodoEntidadAuditoraCamViewModel[];
    habilitarEdicion = true;
    habilitarGuardar = true;
    esVigenteConSolicitudes = false;
    periodoCam: PeriodoCamViewModel;
    private periodoCargado$: Subject<boolean> = new Subject();

    activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const IdPeriodo = params.get('id');
        const IdConf = params.get('idconf');
        if (IdPeriodo) {
            this.IdPeriodo = parseInt(IdPeriodo);
            this.getPeriodoCam(this.IdPeriodo);
        }
        if (IdConf) {
            this.IdConf = parseInt(IdConf);
        }
    });

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private datoscaService: PeriodoDatosCaService,
        private periodoCamService: PeriodoCamService,
        protected notificationService: NotificationService,
        private globalService: GlobalService,
        private usuarioService: UsuariosService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
        private _location: Location,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
        this.habilitarEdicion = this.loggedInUser!.getAction(EnumCodigosTwTcAcciones.MENU_DATOS_CA_Y_ENTIDADES).Editable;

        this.periodoCargado$.subscribe(() => {
            if (this.IdConf) {
                // Llega desde /listadatosca/:id/edit/:idconf (Admin)
                this.getDatosCaPeriodo(this.IdConf);
            }
            //si no viene el 'id' consultarlo con 'params' IdPeriodo e IdCa (UsuarioCA)
            else {
                // Llega desde /mantenimiento/periodos/datosca/:id (Menú para el usuario CA)
                let params = new HttpParams();
                params = params.append('IdPeriodo', this.IdPeriodo);
                params = params.append('IdCa', this.loggedInUser!.TwUsuarioCa.IdCa);
                this.getDatosCaByParams(params);
            }
        });
    }

    private preparePresentation(): void {
        this.getComunidades();
        this.buildForm();
    }

    private buildForm(): void {
        this.form = this.fb.group({
            Id: [0],
            IdPeriodo: [0],
            IdCa: [{ value: null, disabled: true }],
            IdUsuario: [0],
            NombreUsuario: [{ value: '', disabled: true }],
            ApellidosUsuario: [{ value: '', disabled: true }],
            EntidadAuditora: [false],
            ProgramaVerificacion: [false],
            IdUsuarioDefaultAceptacion: null,
            IdUsuarioDefaultUnidadInspeccion: null,
            IdUsuarioDefaultAforoCorte: null,
        });
    }

    private DisableControlsConsulta() {
        this.form.controls['EntidadAuditora'].disable();
        this.form.controls['ProgramaVerificacion'].disable();
    }

    private getComunidades(): void {
        this.globalService.getComunidadesAutonomas().subscribe((comunidades: Tc[]) => {
            this.comunidades = comunidades;
        });
    }

    private getUsuarioCaAceptacion(idCa: number): void {
        this.usuarioService.getUsuariosCaByIdCa(idCa).subscribe(res => {
            this.usuariosCaAceptacion = res;
            this.usuariosCaUnidadInspeccion = res;
            this.usuariosCaAforoCorte = res;
        });
    }

    /**
     * Método que invoca a la API para cargar los datos de un plazo a partir de su id.
     * @param id number
     * @return void
     */
    private getDatosCaPeriodo(id: number): void {
        this.datoscaService
            .getDatosCaPeriodo(id)
            .pipe(
                tap((d: DatosCaPeriodoCamViewModel) => {
                    this.datoscaPeriodo = d;
                    this.IdCampania = d?.IdCampania;
                    this.IdCa = d?.IdCa;
                    this.listaEntidadesPeriodo = this.datoscaPeriodo?.TPeriodoEntidadAuditoraCamCollection;
                    this.navMenuService.updateMenuServiceForPeriodo(d.IdPeriodo);
                    this.manageControlsDisabled();
                    if (!this.habilitarEdicion) {
                        this.DisableControlsConsulta();
                    }
                    //por aqui solo pasa el Admin
                    this.habilitarGuardar = this.addEntidadEnabled;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.form.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    private getDatosCaByParams(params: HttpParams): void {
        this.datoscaService
            .getDatosCaByParams(params)
            .pipe(
                tap((d: DatosCaPeriodoCamViewModel) => {
                    this.datoscaPeriodo = d;
                    this.IdCampania = d?.IdCampania;
                    this.IdCa = d?.IdCa;
                    this.listaEntidadesPeriodo = this.datoscaPeriodo?.TPeriodoEntidadAuditoraCamCollection;
                    this.navMenuService.updateMenuServiceForPeriodo(this.IdPeriodo);

                    //si no tiene datos obtengo el IdCampania
                    if (this.datoscaPeriodo == null) {
                        this.informDatosCaPeriodo();
                    }

                    this.manageControlsDisabled();
                    if (!this.habilitarEdicion) {
                        this.DisableControlsConsulta();
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.form.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Método que invoca a la API para cargar los datos de un periodo a partir de su id.
     * @param id number
     * @return void
     */
    private getPeriodoCam(id: number): void {
        this.periodoCamService
            .getById(id)
            .pipe(
                tap((p: PeriodoCamViewModel) => {
                    this.periodoCam = p;
                    if (this.esPeriodoFinalizado) {
                        this.form.disable();
                        return;
                    }
                    this.getSiVigenteConSolicitudes(id);
                    this.periodoCargado$.next(true);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    private informDatosCaPeriodo(): void {
        this.IdCampania = this.periodoCam?.IdCampania;
        this.datoscaPeriodo = new DatosCaPeriodoCamViewModel();
        this.datoscaPeriodo.IdCampaniaDescripcion = this.periodoCam?.IdCampaniaDescripcion;
        this.datoscaPeriodo.DescripcionPeriodo = this.periodoCam?.DescripcionPeriodo;
        if (this.isCA) {
            this.IdCa = this.loggedInUser!.TwUsuarioCa.IdCa;
            this.datoscaPeriodo.CicloCortoEnabled = this.periodoCam?.CicloCortoEnabled;
            this.datoscaPeriodo.PresentacionAutoEnabled = this.periodoCam?.PresentacionAutoEnabled;
        }
    }

    /**
     * Método que invoca a la API para comprueba si el periodo es vigente y tiene solicitudes de campaña asociadas.
     * @param id number
     * @return void
     */
    private getSiVigenteConSolicitudes(id: number): void {
        this.periodoCamService
            .getPeriodoEsVigente(id)
            .pipe(
                tap((p: boolean) => {
                    this.esVigenteConSolicitudes = p;
                    if (this.esVigenteConSolicitudes) {
                        // this.form.disable();
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    private manageControlsDisabled() {
        if (this.datoscaPeriodo?.EntidadAuditora && !this.esPeriodoFinalizado && !this.esVigenteConSolicitudes) {
            this.form.controls['ProgramaVerificacion'].enable();
            this.addEntidadEnabled = true;
        } else {
            this.form.controls['ProgramaVerificacion'].disable();
            this.addEntidadEnabled = false;
        }

        this.getUsuarioCaAceptacion(this.IdCa);
        // @author: scalero1 - Comento éstas dos llamadas porque se están obteniendo los mismos datos.
        // this.getUsuarioCaUnidadInspeccion(this.IdCa);
        // this.getUsuarioCaAforoCorte(this.IdCa);
        this.removeValidators('IdUsuarioDefaultAceptacion');
        this.removeValidators('IdUsuarioDefaultUnidadInspeccion');
        this.removeValidators('IdUsuarioDefaultAceptacion');

        //RDLF:gestión de obligatoriedad de UsuariosCA si se dan las condiciones
        if (this.datoscaPeriodo.CicloCortoEnabled && this.datoscaPeriodo.PresentacionAutoEnabled) {
            this.usuariosCaAceptacionRequired = true;
            this.usuariosCaUnidadInspeccionRequired = true;
            this.usuariosCaAforoCorteRequired = true;
            this.addValidator('IdUsuarioDefaultAceptacion', Validators.required);
            this.addValidator('IdUsuarioDefaultUnidadInspeccion', Validators.required);
            this.addValidator('IdUsuarioDefaultAforoCorte', Validators.required);
        } else if (this.datoscaPeriodo.CicloCortoEnabled) {
            this.usuariosCaUnidadInspeccionRequired = true;
            this.usuariosCaAforoCorteRequired = true;
            this.addValidator('IdUsuarioDefaultUnidadInspeccion', Validators.required);
            this.addValidator('IdUsuarioDefaultAforoCorte', Validators.required);
        } else if (this.datoscaPeriodo.PresentacionAutoEnabled) {
            this.usuariosCaAceptacionRequired = true;
            this.usuariosCaUnidadInspeccionRequired = true;
            this.addValidator('IdUsuarioDefaultAceptacion', Validators.required);
            this.addValidator('IdUsuarioDefaultUnidadInspeccion', Validators.required);
        }
    }

    onSelectEntidad() {
        if (this.form.value.EntidadAuditora) {
            this.form.controls['ProgramaVerificacion'].enable();
            this.addEntidadEnabled = true;
        } else {
            if (this.listaEntidadesPeriodo != null && this.listaEntidadesPeriodo.length > 0) {
                //mensaje de confirmación
                //TODO rdlf -> eliminar lista de Entidades
                this.confirmationService.confirm({
                    message: '¿Se eliminarán las Entidades Auditoras, desea continuar?',
                    accept: () => {
                        this.form.controls['ProgramaVerificacion'].disable();
                        this.addEntidadEnabled = false;
                        this.form.patchValue({
                            ProgramaVerificacion: false,
                        });
                        //se eliminan las entidades asociadas
                        let longEntidad = this.listaEntidadesPeriodo.length;
                        for (let i = 0; i < longEntidad; i++) {
                            this.onDeleteEntidad(this.listaEntidadesPeriodo[i]);
                        }
                    },
                    reject: () => {
                        this.form.patchValue({
                            EntidadAuditora: true,
                        });
                        return;
                    },
                });
            } else {
                this.form.controls['ProgramaVerificacion'].disable();
                this.addEntidadEnabled = false;
                this.form.patchValue({
                    ProgramaVerificacion: false,
                });
            }
        }
    }

    /**
     * Mostramos la ventana de selección de variedades
     * @return void
     */
    onAddEntidad($event: Event): void {
        $event?.stopPropagation();
        this.onSearchEntidadAuditora();
    }

    onDeleteEntidad(entidad: PeriodoEntidadAuditoraCamViewModel) {
        this.listaEntidadesPeriodo.splice(this.listaEntidadesPeriodo.indexOf(entidad), 1);
    }

    onSearchEntidadAuditora(): void {
        const idCampania = this.IdCampania;
        this.entidadAuditoraPopupComponent.showDialog(idCampania);
    }

    onAceptDialogEntidadAuditora(entidadAuditora: RegEntidadAuditoraItemViewModel): void {
        if (this.listaEntidadesPeriodo == undefined) {
            this.listaEntidadesPeriodo = [];
        }
        if (this.datoscaPeriodo?.TPeriodoEntidadAuditoraCamCollection != null) {
            this.listaEntidadesPeriodo = this.datoscaPeriodo?.TPeriodoEntidadAuditoraCamCollection;
        }
        //Comprobamos que no esté ya incluido
        let yaIncluido = this.listaEntidadesPeriodo?.some(s => s.IdEntidadAuditora == entidadAuditora.Id);
        if (yaIncluido) {
            return;
        }
        let entidadPeriodo = new PeriodoEntidadAuditoraCamViewModel();
        entidadPeriodo.IdPeriodoDatosCa = this.IdPeriodo;
        entidadPeriodo.IdEntidadAuditora = entidadAuditora.Id;
        entidadPeriodo.RazonSocialEa = entidadAuditora.RazonSocial;
        entidadPeriodo.NifEa = entidadAuditora.NIFRazonSocial;
        entidadPeriodo.ProvinciaEa = entidadAuditora.IdProvinciaRazonSocialDescripcion;
        entidadPeriodo.LocalidadEa = entidadAuditora.LocalidadRazonSocial;
        this.listaEntidadesPeriodo.push(entidadPeriodo);
    }

    /**
     * Invocación de la API para salvar los cambios realizados en el periodo
     * @return void
     */
    onSave(): void {
        if (this.form.invalid) {
            this.addErrorMessage('Revise los datos antes de guardar');
            return;
        }

        //Valido que haya entidades en la colección si lo has seleccionado
        if (this.form.value.EntidadAuditora && (this.listaEntidadesPeriodo == undefined || this.listaEntidadesPeriodo.length == 0)) {
            this.addErrorMessage('Ha seleccionado Entidades Auditoras, debe añadir alguna a la lista');
            return;
        }

        //Si quito el check 'Programa de Verificación' hay que comprobar que no esté ya presentado por alguna EA
        if (this.form.value.ProgramaVerificacion === false) {
            if (
                this.datoscaPeriodo?.TPeriodoEntidadAuditoraCamCollection != null &&
                this.datoscaPeriodo.TPeriodoEntidadAuditoraCamCollection.some(
                    x => x.TwProgramaVerificacionCam != null && x.TwProgramaVerificacionCam.IdEstadoPv != EnumIdsTcEstadoPvCam.ID_NO_PRESENTADO,
                )
            ) {
                this.addErrorMessage('No puede realizar esta acción. Ya hay Programas de Verificación tramitados');
                return;
            }
        }

        this.visibilitySpinner = true;
        const model: DatosCaPeriodoCamViewModel = this.form.getRawValue();

        model.IdPeriodo = this.IdPeriodo;
        model.IdUsuario = this.datoscaPeriodo?.IdUsuario || this.loggedInUser!.Id;
        model.IdCa = this.datoscaPeriodo?.IdCa || this.loggedInUser!.TwUsuarioCa.IdCa;

        model.TPeriodoEntidadAuditoraCamCollection = this.listaEntidadesPeriodo;

        this.datoscaService
            .save(model)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.onBack();
                }),
                catchError(error => {
                    this.visibilitySpinner = false;
                    this.catchServerError(error);

                    //if (error?.message?.indexOf(this.messageErrorAlreadyExist) != -1) {
                    //    this.form.disable();
                    //}
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        //this.router.navigate([this.listUrl + this.IdPeriodo]);
        //Usamos el location ya que hay diferentes formas de llegar aquí­
        this._location.back();
    }

    get esPeriodoFinalizado(): boolean {
        const hoy = new Date();
        if (!this.periodoCam?.FechaFin) {
            return false;
        }
        const fechaFinPeriodo = new Date(this.periodoCam.FechaFin);
        return hoy >= fechaFinPeriodo;
    }

    get formInvalid(): boolean {
        return !this.form.valid || !this.form.dirty || this.form.pristine;
    }
}
